import { LevelContext } from "features/qualityForm/common/Contexts";
import {
    EntityType,
    selectQuestionSequenceNumber,
    VisibilityConflictsOnMove,
} from "features/qualityForm/qualityFormSlice";
import { useStrings } from "features/i18n/useStrings";
import { useContext } from "react";

import { ErrorAction, ErrorBox } from "./VisibilityConflictErrorBox";
import strings from "./VisibilityConflictErrorBox.strings";
import { useAppSelector } from "app/hooks";

function MoveQuestionConflicts({
    questionId,
    userErrorAction,
    entityType,
}: {
    questionId: string;
    userErrorAction: ErrorAction;
    entityType: EntityType;
}) {
    const t = useStrings(strings);

    const referredEntityLevel = useContext(LevelContext);
    const referredSequenceNumber =
        referredEntityLevel.level.toString() +
        (entityType === EntityType.Question
            ? "." + referredEntityLevel.subLevel.toString()
            : "");
    const questionSequenceNumber = useAppSelector((state) =>
        selectQuestionSequenceNumber(state.qualityForm)(questionId)
    );

    const errorMessage = t.labels.visibilityConditionErrorMessage[
        userErrorAction
    ]({
        referredEntityType: t.labels.entityType.move[entityType],
        dependentEntityType: t.labels.entityType[EntityType.Question],
        dependentSequenceNumber:
            questionSequenceNumber?.questionGroupNumber +
            "." +
            questionSequenceNumber?.questionSequenceNumber,
        referredSequenceNumber,
    });
    return <li>{errorMessage}</li>;
}

function MoveQuestionGroupConflicts({
    dependentQuestionId,
    conditionalQuestionId,
    userErrorAction,
    entityType,
}: {
    dependentQuestionId: string;
    conditionalQuestionId: string;
    userErrorAction: ErrorAction.MoveDown | ErrorAction.MoveUp;
    entityType: EntityType;
}) {
    const t = useStrings(strings);

    const referredEntityLevel = useContext(LevelContext);
    const referredSequenceNumber = referredEntityLevel.level.toString();
    const dependentQuestionSequenceNumber = useAppSelector((state) =>
        selectQuestionSequenceNumber(state.qualityForm)(dependentQuestionId)
    );
    const condtionalQuestionIdSequenceNumber = useAppSelector((state) =>
        selectQuestionSequenceNumber(state.qualityForm)(conditionalQuestionId)
    );

    const errorMessage =
        t.labels.visibilityConditionErrorMessageForQuestionGroup[
            userErrorAction
        ]({
            referredEntityType: t.labels.entityType.move[entityType],
            dependentSequenceNumber:
                dependentQuestionSequenceNumber?.questionGroupNumber +
                "." +
                dependentQuestionSequenceNumber?.questionSequenceNumber,
            referredSequenceNumber,
            conditionalEntitySequenceNumber:
                condtionalQuestionIdSequenceNumber?.questionGroupNumber +
                "." +
                condtionalQuestionIdSequenceNumber?.questionSequenceNumber,
        });

    return <li>{errorMessage}</li>;
}

export function MovingVisibilityConflictErrorBox({
    visibilityConflictsOnMove,
    entityType,
    userErrorAction,
    clearVisibilityConflicts,
}: {
    visibilityConflictsOnMove: VisibilityConflictsOnMove;
    entityType: EntityType;
    userErrorAction: ErrorAction.MoveDown | ErrorAction.MoveUp;
    clearVisibilityConflicts: () => void;
}) {
    const questionErrorListItems = visibilityConflictsOnMove.questionIds.map(
        (q) => (
            <MoveQuestionConflicts
                questionId={q}
                userErrorAction={userErrorAction}
                entityType={entityType}
                key={q}
            />
        )
    );

    const questionWithinQuestionGroupErrorListItems = Object.keys(
        visibilityConflictsOnMove.conflictingQuestionsWithinQuestionGroup ?? {}
    ).map((dependentQuestionId) => {
        return visibilityConflictsOnMove.conflictingQuestionsWithinQuestionGroup?.[
            dependentQuestionId
        ].map((conditionalQuestionId) => (
            <MoveQuestionGroupConflicts
                dependentQuestionId={dependentQuestionId}
                conditionalQuestionId={conditionalQuestionId}
                userErrorAction={userErrorAction}
                entityType={entityType}
                key={dependentQuestionId}
            />
        ));
    });
    return (
        <ErrorBox
            errorMessages={
                <>
                    {questionErrorListItems}
                    {questionWithinQuestionGroupErrorListItems}
                </>
            }
            clearSourceError={clearVisibilityConflicts}
        />
    );
}
